import { Injectable } from '@angular/core';
import {
  isLtiParams,
  LogoutResponse,
  LtiParams,
  ReaderApiClientError,
  TTSAzureRawTextRequest,
  TTSAzureRequest,
  TTSChunk,
  TTSChunkCollection,
  TTSRawTextTranslationResponse,
} from '@mhe/reader/models';
import { Observable } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import * as readerConfigQuery from '@mhe/reader/core/state/configuration/configuration.selectors';
import { IReaderClient } from '../../models/reader.client.model';
import { TtsApiClient } from '../tts-api/tts-api.client.service';
import { FeatureTogglesService } from '@mhe/reader/features/feature-toggles.service';

@Injectable({ providedIn: 'root' })
export class ReaderApiClient implements IReaderClient {
  private readonly baseUrl$: Observable<string | undefined>;

  constructor(
    store: Store,
    private readonly http: HttpClient,
    private readonly ttsApiClient: TtsApiClient,
    private readonly featureTogglesService: FeatureTogglesService,
  ) {
    // TODO support dark Reader API URL
    this.baseUrl$ = store.pipe(
      select(readerConfigQuery.getReaderApi),
      filter((s: string | undefined) => typeof s === 'string' && s.length > 0),
      first(),
    );
  }

  fetchLTIParams(): Observable<LtiParams> {
    return this.baseUrl$.pipe(
      mergeMap((baseUrl) =>
        this.http.get<LtiParams>(`${baseUrl}/v1/lti`, {
          withCredentials: true,
        }),
      ),
      first(),
      map((rsp) => {
        if (!isLtiParams(rsp)) {
          throw new ReaderApiClientError(
            'get',
            '/lti',
            'Response is not a valid set of LTI parameters',
          );
        }
        return rsp;
      }),
    );
  }

  /**
   * Obtains status on an existing text-to-speech job. The resonse contains
   * multiple ordered chunks
   */
  fetchTextToSpeechJob(
    requestCacheKey: string,
  ): Observable<TTSChunkCollection> {
    return this.featureTogglesService.isTtsApiEnabled$.pipe(
      first(),
      mergeMap((isTtsApiEnabled) => {
        if (isTtsApiEnabled) {
          return this.ttsApiClient.fetchTextToSpeechJob(requestCacheKey);
        }

        return this.baseUrl$.pipe(
          mergeMap((readerApiBaseUrl) => {
            const url = `${readerApiBaseUrl}/v1/tts/chunk/status?requestCacheKey=${requestCacheKey}`;
            return this.http.post<TTSChunkCollection>(url, {});
          }),
        );
      }),
    );
  }

  /**
   * Sends content to be translated for text-to-speech. In
   * the response, the first chunk mp3 url is provided and
   * the metadata.requestCacheKey is used to poll an endpoint
   * for completion of the remaining chunks.
   */
  createTextToSpeechJob(params: TTSAzureRequest, tocItemId?: string): Observable<TTSChunk | null> {
    return this.featureTogglesService.isTtsApiEnabled$.pipe(
      first(),
      mergeMap((isTtsApiEnabled) => {
        if (isTtsApiEnabled) {
          return this.ttsApiClient.createTextToSpeechJob(params, tocItemId);
        }

        return this.baseUrl$.pipe(
          mergeMap((readerApiBaseUrl) => {
            const url = `${readerApiBaseUrl}/v1/tts/chunk`;
            const urlencoded = new URLSearchParams();
            Object.keys(params).forEach(key => urlencoded.append(key, params[key]));
            return this.http.post<TTSChunk>(url, urlencoded, {
              headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
            });
          }),
        );
      }),
    );
  }

  translateRawTextToSpeech(
    params: TTSAzureRawTextRequest,
  ): Observable<TTSRawTextTranslationResponse> {
    return this.featureTogglesService.isTtsApiEnabled$.pipe(
      first(),
      mergeMap((isTtsApiEnabled) => {
        if (isTtsApiEnabled) {
          return this.ttsApiClient.translateRawTextToSpeech(params);
        }

        return this.baseUrl$.pipe(
          mergeMap((readerApiBaseUrl) => {
            const url = `${readerApiBaseUrl}/v1/tts`;
            const urlencoded = new URLSearchParams();
            Object.keys(params).forEach(key => urlencoded.append(key, params[key]));
            return this.http.post<TTSRawTextTranslationResponse>(url, urlencoded, {
              headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
            });
          }),
        );
      }),
    );
  }

  logout(): Observable<LogoutResponse> {
    return this.baseUrl$.pipe(
      mergeMap((baseUrl) => {
        // TODO Reader API needs to implement a logout endpoint
        return this.http.get<LogoutResponse>(`${baseUrl}/v1/logout`, {
          withCredentials: true,
        });
      }),
    );
  }
}
